import React from "react";
import { editBusinessSheet } from "../api/businessSheet";

export const createUpdateBusinessSheetData = (
  setBusinessSheetData,
  setValue,
  apiFn = editBusinessSheet,
) => {
  return async (updatedData) => {
    console.log("Updating fields:", Object.keys(updatedData));

    // Update the React state directly for immediate UI feedback
    setBusinessSheetData((currentData) => ({
      ...currentData,
      ...updatedData,
    }));

    // Perform the API update
    await apiFn(updatedData);

    // Update localStorage with the latest state
    setValue("businessSheetData", (businessSheetData) => ({
      ...businessSheetData,
      ...updatedData,
    }));
  };
};


/**
 * Filters out specified fields from a business sheet data object
 * 
 * @param {Object} data - The business sheet data to filter
 * @param {string[]} excludedFields - Array of field names to exclude
 * @returns {Object} - Filtered business sheet data
 */
export const filterBusinessSheetData = (data, excludedFields) => {
  if (!data) return {};
  
  const filtered = {...data};
  excludedFields.forEach(field => {
    delete filtered[field];
  });
  
  return filtered;
};


/**
 * Normalizes array field values to match required length
 * 
 * @param {string[]} items - The array of items to normalize
 * @param {number} maxItems - The required length of the array
 * @param {string} fieldName - The name of the field (for logging)
 * @returns {string[]} - Normalized array of exactly maxItems length
 */
const URL_REGEX = /https?:\/\/[^\s　]+/g;

const linkifyLine = (line, lineKey) => {
  const parts = [];
  let lastIndex = 0;
  const regex = new RegExp(URL_REGEX.source, "g");
  let match;
  while ((match = regex.exec(line)) !== null) {
    if (match.index > lastIndex) {
      parts.push(line.slice(lastIndex, match.index));
    }
    parts.push(
      React.createElement(
        "a",
        {
          key: `${lineKey}-${match.index}`,
          href: match[0],
          target: "_blank",
          rel: "noopener noreferrer",
          onClick: (e) => e.stopPropagation(),
        },
        match[0],
      ),
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < line.length) {
    parts.push(line.slice(lastIndex));
  }
  return parts.length > 0 ? parts : [line];
};

/**
 * テキスト中の URL を別タブで開くリンクに変換する（改行・空白はそのまま保持）。
 * 改行の表示は呼び出し側の CSS（white-space: pre-wrap 等）に委ねる。
 */
export const linkifyText = (text, keyPrefix = "link") =>
  text ? linkifyLine(text, keyPrefix) : text;

export const renderTextWithLineBreaks = (text) => {
  if (!text || text.trim() === "") return null;

  return text
    .replace(/　+/g, "\n")
    .split("\n")
    .map((line, i) =>
      line.trim() === ""
        ? React.createElement("br", { key: i })
        : React.createElement(
            "span",
            { key: i },
            ...linkifyLine(line, i),
            React.createElement("br"),
          ),
    );
};

/**
 * Issue #91: 金のシリーズ／パワーワード／アイテムの構造化仕様。
 * バックエンド（domain/entities/BusinessSheet.ts の SHEET_ITEM_SPECS）と同値。
 */
export const SHEET_ITEM_SPECS = {
  goldenSeries: {
    count: 3,
    fields: [
      { key: "title", label: "悩みタイトル", maxLength: 40 },
      { key: "content", label: "内容", maxLength: 40 },
      { key: "approach", label: "アプローチ", maxLength: 40 },
    ],
  },
  powerWord: {
    count: 6,
    fields: [
      { key: "title", label: "タイトル", maxLength: 20 },
      { key: "content", label: "内容", maxLength: 40 },
    ],
  },
  itemProduct: {
    count: 3,
    fields: [
      { key: "title", label: "タイトル", maxLength: 20 },
      { key: "url", label: "URL", maxLength: 500 },
      { key: "content", label: "内容", maxLength: 40 },
    ],
  },
};

/** ビジネスシートのフィールド名 → 構造化アイテム種別 */
export const SHEET_ITEM_KIND_BY_FIELD = {
  goldenEgg: "goldenSeries",
  goldenGoose: "goldenSeries",
  goldenFarmer: "goldenSeries",
  powerWords: "powerWord",
  itemsProducts: "itemProduct",
};

const emptySheetItem = (kind) =>
  SHEET_ITEM_SPECS[kind].fields.reduce(
    (acc, field) => ({ ...acc, [field.key]: "" }),
    {},
  );

/** 1要素をオブジェクトへ正規化する。文字列は先頭キー（title）に載せる。 */
export const normalizeSheetItem = (item, kind) => {
  const spec = SHEET_ITEM_SPECS[kind];
  const base = emptySheetItem(kind);

  if (typeof item === "string") {
    return { ...base, [spec.fields[0].key]: item };
  }
  if (item && typeof item === "object" && !Array.isArray(item)) {
    return spec.fields.reduce(
      (acc, field) => ({
        ...acc,
        [field.key]: item[field.key] == null ? "" : String(item[field.key]),
      }),
      {},
    );
  }
  return base;
};

/**
 * 表示・編集・localStorage 復元の入口で使う正規化。
 * レガシーな文字列配列・null・件数不足/超過をすべて吸収し、冪等。
 */
export const normalizeSheetItems = (items, kind) => {
  const { count } = SHEET_ITEM_SPECS[kind];
  const source = Array.isArray(items) ? items : [];

  return Array.from({ length: count }, (_, i) =>
    normalizeSheetItem(source[i], kind),
  );
};

/** 既定件数の空オブジェクト配列（新規作成フォームの初期値用） */
export const createEmptySheetItems = (kind) => normalizeSheetItems([], kind);

export const normalizeArrayField = (items, maxItems, fieldName, kind) => {
  // Issue #91: 構造化フィールドはオブジェクト配列として正規化する
  if (kind && SHEET_ITEM_SPECS[kind]) {
    if (!Array.isArray(items)) {
      console.error(`Field ${fieldName} expects an array, received ${typeof items}`);
    }
    return normalizeSheetItems(items, kind);
  }

  if (!Array.isArray(items)) {
    console.error(`Field ${fieldName} expects an array, received ${typeof items}`);
    return Array(maxItems).fill("");
  }
  
  // Create a new array of exactly the required length
  const normalized = Array(maxItems).fill("");
  
  // Fill in values from the provided items array (up to maxItems)
  items.slice(0, maxItems).forEach((item, index) => {
    normalized[index] = item || ""; // Handle null/undefined items
  });
  
  // Log appropriate warnings
  if (items.length > maxItems) {
    console.warn(`Field ${fieldName} limited to ${maxItems} items. ${items.length - maxItems} items were truncated.`);
  } else if (items.length < maxItems) {
    console.warn(`Field ${fieldName} requires ${maxItems} items. ${maxItems - items.length} empty items were added.`);
  }
  
  return normalized;
};
